import { fillPath, route } from "./routes.js";
import { z } from "zod";
//#region src/auth-providers.ts
/**
* `/v1/admin/auth-providers*` — the five single-sign-on routes the admin app's auth-providers page
* calls.
*
* Producer: `AdminAuthProvidersController` and `AuthProviderService` in
* `cloud/dp/core-api/src/admin/`. Consumer: the admin app's `AdminClient`. The shell's read types
* differ from the controller in three places:
*
* 1. `AuthProviderService.toApiResponse` returns `{ id, type, name, enabled, priority, config }`
*    plus `oauthProvider` for a non-builtin type. The shell's `AuthProviderDetail` additionally
*    declares `clientId`, `clientSecret` and `hasClientSecret`, none of which the server sends —
*    OAuth credentials live inside `config`.
* 2. The shell types `AuthProviderDetail.type` as `'password' | 'passkey' | 'oauth'`. The wire
*    value is the provider type itself (`password`, `passkey`, `google`, `github`, …), which is
*    `providerType` renamed. `'oauth'` is never a value of this key.
* 3. `getCatalog` spreads the whole `ProviderTypeDefinition`, so `defaultEnabled`,
*    `defaultPriority` and `singletonPerOrg` are on the wire. The shell's `CatalogEntry` extends
*    `ProviderCatalogEntry`, which declares none of the three.
*
* Permission keys: the class default is `settings:identity:manage` because writing here
* reconfigures the org's single sign-on; the two GETs override it to `settings:identity:read`,
* which is safe because both responses run through `maskSecrets`.
*
* `DELETE /v1/admin/auth-providers/{id}` answers 204 with no body, so it has no response schema.
*
* Direction rule (R-28): every response schema here is `z.object`, so a member core-api adds is
* stripped rather than refused and one new column cannot red every page that reads the route.
* Every request schema is `z.strictObject`, so a key the server would reject is refused before
* the request leaves the client. No shape in this file travels in both directions.
*/
/**
* One provider-specific configuration field in a catalog entry. Drives the form the page renders.
*
* `isSecret` is what `maskSecrets` reads: a secret field's value comes back as a mask rather than
* the stored value, and `extractSecrets` writes it to the secret store under a `<key>Ref` name.
*
* Owner: `ConfigFieldDefinition` in `cloud/dp/core-api/src/identity/provider-catalog.ts`.
*/
const authProviderConfigFieldSchema = z.object({
	key: z.string(),
	label: z.string(),
	type: z.enum([
		"text",
		"password",
		"url",
		"textarea",
		"multiselect"
	]),
	required: z.boolean(),
	placeholder: z.string().optional(),
	helpText: z.string().optional(),
	isSecret: z.boolean()
});
/**
* One entry of `GET /v1/admin/auth-providers/catalog`: a `ProviderTypeDefinition` spread whole,
* plus the three keys the admin route adds from the org's existing providers.
*
* `configured` is present on every entry. `enabled` and `providerId` are present only when the org
* has a provider of that type, so an absent `enabled` means "not configured", never "configured and
* off" — read `configured` first.
*
* `singletonPerOrg` is why a second provider of the same type is a 409 on create.
*
* Owner: `AuthProviderService.getCatalog` and `ProviderTypeDefinition` in
* `cloud/dp/core-api/src/identity/provider-catalog.ts`.
*/
const authProviderCatalogEntrySchema = z.object({
	type: z.string(),
	displayName: z.string(),
	category: z.enum([
		"builtin",
		"oauth",
		"enterprise"
	]),
	description: z.string(),
	icon: z.string(),
	configFields: z.array(authProviderConfigFieldSchema),
	defaultEnabled: z.boolean(),
	defaultPriority: z.number().int(),
	singletonPerOrg: z.boolean(),
	setupGuide: z.string().optional(),
	/**
	* The provider type's OAuth endpoints, present only where the data plane can complete the flow:
	* `google` and `github`. Absent for the builtins, which refuse OAuth. `userInfoUrl` is where the
	* access token is first spent; it describes the call and does not drive it. The data plane reads
	* the profile through a per-type userinfo strategy that is not on the wire, and GitHub's strategy
	* takes the address from `/user/emails`, not from this URL's unverified `email` (#3053). These
	* are constants of the platform, not tenant settings: they used to be read
	* off each org's `config` row with Google's endpoints as the fallback for every one of them,
	* which made `authorizationUrl`, `tokenUrl`, `userInfoUrl` and `scopes` the only way a
	* non-Google provider worked while no catalog entry declared them (#3004). They are published
	* here because an operator configuring a provider should be able to see where their users will
	* be sent; all four are public, documented URLs.
	*/
	oauth: z.object({
		authorizationUrl: z.url(),
		tokenUrl: z.url(),
		userInfoUrl: z.url(),
		scopes: z.array(z.string())
	}).optional(),
	configured: z.boolean(),
	enabled: z.boolean().optional(),
	providerId: z.uuid().optional()
});
/**
* One configured provider as `toApiResponse` shapes it — the body of the list, the create and the
* update.
*
* `type` is the row's `providerType` renamed, so it is the provider type string, not a three-value
* union. `name` is the row's `displayName` renamed. `oauthProvider` is derived, present only when
* `type` is neither `password` nor `passkey`, and equal to `type` when present.
*
* `config` carries the provider's settings with every secret field replaced by a mask, so it is
* safe under `settings:identity:read`. It is a `jsonb` column and its inner keys are the provider
* type's own, which is why it is an open record here rather than a fixed shape.
*
* Owner: `AuthProviderService.toApiResponse`.
*/
const authProviderDetailSchema = z.object({
	id: z.uuid(),
	type: z.string(),
	name: z.string().nullable(),
	enabled: z.boolean(),
	priority: z.number().int(),
	config: z.record(z.string(), z.unknown()),
	oauthProvider: z.string().optional()
});
/**
* `GET /v1/admin/auth-providers/catalog` — every provider type, joined to what the org has
* configured. Requires `settings:identity:read`.
*/
const GET_AUTH_PROVIDER_CATALOG_ROUTE = route("GET", "/v1/admin/auth-providers/catalog");
/**
* `GET /v1/admin/auth-providers` — the org's configured providers, secrets masked. Requires
* `settings:identity:read`.
*/
const LIST_AUTH_PROVIDERS_ROUTE = route("GET", "/v1/admin/auth-providers");
/**
* `POST /v1/admin/auth-providers` — configure a provider. Status 201; 400 on an unknown type, 409
* when a singleton type is already configured. Requires `settings:identity:manage`.
*/
const CREATE_AUTH_PROVIDER_ROUTE = route("POST", "/v1/admin/auth-providers");
/**
* `PATCH /v1/admin/auth-providers/{id}` — update one provider. Status 200; 404 when it does not
* exist, 422 when it is the last enabled provider and the body disables it. Requires
* `settings:identity:manage`.
*/
const UPDATE_AUTH_PROVIDER_ROUTE = route("PATCH", "/v1/admin/auth-providers/{id}");
/**
* `DELETE /v1/admin/auth-providers/{id}` — remove one provider. Status 204 with no body; 404 when
* it does not exist, 422 when it is the last enabled provider. Requires
* `settings:identity:manage`.
*/
const DELETE_AUTH_PROVIDER_ROUTE = route("DELETE", "/v1/admin/auth-providers/{id}");
/**
* Fill `/v1/admin/auth-providers/{id}`, which `PATCH` and `DELETE` share. The placeholder is `id`,
* the name `@Param('id')` puts in `openapi.json`.
*/
function adminAuthProviderPath(providerId) {
	return fillPath(UPDATE_AUTH_PROVIDER_ROUTE.path, { id: providerId });
}
/**
* The body of `GET /v1/admin/auth-providers/catalog`. A bare array, in `PROVIDER_CATALOG` order.
*/
const getAuthProviderCatalogResponseSchema = z.array(authProviderCatalogEntrySchema);
/** The body of `GET /v1/admin/auth-providers`. A bare array. */
const listAuthProvidersResponseSchema = z.array(authProviderDetailSchema);
/**
* The body of `POST /v1/admin/auth-providers`.
*
* `type` must name an entry in `PROVIDER_CATALOG` or the service answers 400. `config` is the
* provider's own settings, validated field by field against that entry by `validateConfigFields`,
* so a shape rule here would duplicate a per-type rule and could only be wrong.
*
* `config` is closed against the catalog entry: a key no `configFields` entry declares is a 400
* naming it (#3004). Build it from `configFields`, never by spreading a provider read back from
* `GET /v1/admin/auth-providers` — that response carries `has<Field>` and `<field>Ref`, and neither
* is a catalog field.
*
* `enabled` is optional and defaults to the catalog entry's `defaultEnabled`. `displayName` and
* `priority` are not accepted on create: both come from the catalog entry.
*
* Owner: `createAuthProviderSchema` in `admin-auth-providers.controller.ts`.
*/
const createAuthProviderRequestSchema = z.strictObject({
	type: z.string(),
	config: z.record(z.string(), z.unknown()),
	enabled: z.boolean().optional()
});
/**
* The body of `POST /v1/admin/auth-providers` — status 201, the saved provider with secrets
* masked.
*/
const createAuthProviderResponseSchema = authProviderDetailSchema;
/**
* The body of `PATCH /v1/admin/auth-providers/{id}` — four keys and no more.
* `ZionStrictValidationPipe` deep-stricts the bound schema, so any other top-level key is a 400.
*
* `config` is nested, not flat. The shell page once sent provider settings such as `clientId` and
* `clientSecret` at the top level, because the request was typed as the response DTO, and every
* provider-config save was a 400. A read type reused as a write type can only say what the server
* returns.
*
* `type` is absent: a provider's type is fixed once configured.
*
* `config` is closed against the provider's catalog entry on this route too (#3004), so the
* response-as-request mistake above now fails a second way: `hasClientSecret` and `clientSecretRef`
* from a GET response are not catalog fields and the service answers 400 naming them.
*
* A successful write also STRIPS any undeclared key the stored row already carried, so a provider
* configured before #3004 loses its legacy `authorizationUrl`, `tokenUrl`, `userInfoUrl` or
* `scopes` on its next config write. The server logs the stripped key names at warn. Declared keys
* and the `<field>Ref` the server itself stores for a secret field are kept.
*
* Owner: `updateAuthProviderSchema` in `admin-auth-providers.controller.ts`.
*/
const updateAuthProviderRequestSchema = z.strictObject({
	displayName: z.string().optional(),
	enabled: z.boolean().optional(),
	config: z.record(z.string(), z.unknown()).optional(),
	priority: z.number().optional()
});
/** The body of `PATCH /v1/admin/auth-providers/{id}` — the saved provider with secrets masked. */
const updateAuthProviderResponseSchema = authProviderDetailSchema;
/** Every route this file names, for `route-census.ts`. */
const authProvidersRoutes = Object.freeze([
	GET_AUTH_PROVIDER_CATALOG_ROUTE,
	LIST_AUTH_PROVIDERS_ROUTE,
	CREATE_AUTH_PROVIDER_ROUTE,
	UPDATE_AUTH_PROVIDER_ROUTE,
	DELETE_AUTH_PROVIDER_ROUTE
]);
//#endregion
export { CREATE_AUTH_PROVIDER_ROUTE, DELETE_AUTH_PROVIDER_ROUTE, GET_AUTH_PROVIDER_CATALOG_ROUTE, LIST_AUTH_PROVIDERS_ROUTE, UPDATE_AUTH_PROVIDER_ROUTE, adminAuthProviderPath, authProviderCatalogEntrySchema, authProviderConfigFieldSchema, authProviderDetailSchema, authProvidersRoutes, createAuthProviderRequestSchema, createAuthProviderResponseSchema, getAuthProviderCatalogResponseSchema, listAuthProvidersResponseSchema, updateAuthProviderRequestSchema, updateAuthProviderResponseSchema };

//# sourceMappingURL=auth-providers.js.map